import {
  boardForViewer,
  MY_WORK_BOARD,
  MY_WORK_LAYOUT_VERSION,
  type ManagedBoardWidget,
  type MyWorkWidgetKey,
} from "@salestrak/widgets";
import { AGE_STATUS_CONFIG } from "#app/lib/age-status";
import { type ManagedWidget, withManagedLayoutVersion } from "./managed-widgets";

export { MY_WORK_LAYOUT_VERSION };

/**
 * Display copy for each My Work board entry. The board DATA (types, configs,
 * layout, defaultOn) lives in @salestrak/widgets — the same
 * module the core API seeds first reads from — so only presentation is defined
 * here. The `MyWorkWidgetKey` union makes a new board entry a typecheck error
 * until it gets copy.
 */
const PRESENTATION: Record<
  MyWorkWidgetKey,
  Pick<ManagedWidget, "label" | "description" | "color">
> = {
  "my-work": {
    label: "Assigned to me",
    description:
      "Every Project assigned to you in one list — name, type and assignees, most recently updated first.",
    color: "violet",
  },
  "my-activity": {
    label: "Activity on my work",
    description:
      "What others did on the Projects assigned to you — stage moves, comments, edits — newest first.",
    color: "blue",
  },
  "needs-attention": {
    label: "Follow ups",
    description: `Every Project Follow up you own — banded ${AGE_STATUS_CONFIG.offTrack.label}, ${AGE_STATUS_CONFIG.atRisk.label} and ${AGE_STATUS_CONFIG.onTrack.label}, the nearest deadline first.`,
    color: "orange",
  },
  "my-pipeline": {
    label: "My stage mix",
    description: "The share of your Projects in each stage, as one battery bar.",
    color: "green",
  },
  notepad: {
    label: "Notepad",
    description: "A private space for quick notes and reminders.",
    color: "light-purple",
  },
  recent: {
    label: "Recent",
    description:
      "What you opened last — Projects and dashboards, newest first.",
    color: "pink",
  },
  "my-outcomes": {
    label: "My Update outcomes",
    description: "How your Project Updates ended, and your positive rate.",
    color: "green",
  },
  agenda: {
    label: "Agenda",
    description: "Your upcoming calendar events, once a calendar is connected.",
    color: "light-blue",
  },
};

function managedEntry(entry: ManagedBoardWidget<MyWorkWidgetKey>): ManagedWidget {
  return {
    ...entry,
    config: withManagedLayoutVersion(MY_WORK_LAYOUT_VERSION, entry.config),
    ...PRESENTATION[entry.widgetKey],
  };
}

/**
 * My Work is a curated personal home, not a reporting dashboard. Its 12-column
 * storage grid deliberately uses only 6- and 12-column spans, producing a stable
 * two-column rhythm of half- and full-width snapshots.
 */
export const MY_WORK_WIDGETS: ManagedWidget[] = MY_WORK_BOARD.map(managedEntry);

/**
 * The catalog a guest-tier viewer gets: the `guestHidden` update-verb cards
 * (My Update outcomes, Update needed) drop out — a Guest can never post
 * an Update, so each would render a permanently-empty instrument. Passing
 * this catalog also cleans a board a fuller catalog already seeded: the
 * managed migration retires a placed widget whose key the catalog no longer
 * offers, and the retirement closes the slots it vacates.
 */
export const GUEST_MY_WORK_WIDGETS: ManagedWidget[] = boardForViewer(MY_WORK_BOARD, {
  guestTier: true,
}).map(managedEntry);
